import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Controller, FieldValues } from 'react-hook-form';
import { IMask, IMaskInput } from 'react-imask';

import useTranslate from '@/hooks/useTranslate';
import useScrollIntoView from '@/hooks/useValidateAddress/useScrollIntoView';
import { COMPONENTS_SHARED_PATH_TRANSLATION_KEY } from '@/pages/members/pages/Onboarding/constants';
import FieldError from '@/ui/members/components/form/FieldError';
import Label from '@/ui/members/components/form/Label';
import { focusClasses } from '@/ui/members/components/form/styles';

import { Props } from './types';

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Converts a date string from MM/DD/YYYY format to YYYY-MM-DD format
 * Only converts if the date is complete (no underscores or missing parts)
 */
const convertToISOFormat = (dateString: string): string => {
  if (!dateString || dateString.length !== 10) return '';

  // Check for incomplete input (contains underscores)
  if (dateString.includes('_')) return '';

  const [month, day, year] = dateString.split('/');
  if (!month || !day || !year) return '';

  // Ensure all parts are complete numbers
  if (month.length !== 2 || day.length !== 2 || year.length !== 4) return '';

  return `${year}-${month}-${day}`;
};

/**
 * Converts a date string from YYYY-MM-DD format to MM/DD/YYYY format
 */
const convertFromISOFormat = (isoDateString: string): string => {
  if (!isoDateString) return '';

  const [year, month, day] = isoDateString.split('-');
  if (!year || !month || !day) return '';

  return `${month}/${day}/${year}`;
};

type MaskedDateInputProps = {
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  label: string;
};

const MaskedDateInput = ({ value, onChange, onBlur, label }: MaskedDateInputProps) => {
  const [displayValue, setDisplayValue] = useState(() => convertFromISOFormat(value || ''));
  const [hasStartedTyping, setHasStartedTyping] = useState(() => !!value);

  // Track previous field value to detect external changes
  const prevFieldValueRef = useRef(value);
  // Track whether the last change came from user input
  const isUserInputRef = useRef(false);

  const scrollIntoView = useScrollIntoView({
    enabled: true,
    offset: 0
  });

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    scrollIntoView(e);
  };

  const handleBlur = () => {
    // Reset typing state if input is empty or only contains mask placeholders
    const isEffectivelyEmpty =
      !displayValue ||
      displayValue.trim() === '' ||
      displayValue.replace(/[_/]/g, '').trim() === '';

    if (isEffectivelyEmpty) {
      setHasStartedTyping(false);
      setDisplayValue(''); // Clear the display value to show placeholder
    }
    onBlur();
  };

  // Update display value when form value changes externally (e.g., form reset, clear)
  // But NOT when the change came from user input (typing/backspacing)
  useEffect(() => {
    if (prevFieldValueRef.current !== value) {
      // Only sync display from form if this wasn't triggered by user input
      if (!isUserInputRef.current) {
        const formattedValue = convertFromISOFormat(value || '');
        setDisplayValue(formattedValue);
        // Update typing state based on whether there's a value
        setHasStartedTyping(!!formattedValue);
      }
      prevFieldValueRef.current = value;
      // Reset the flag after processing
      isUserInputRef.current = false;
    }
  }, [value]);

  // Show full mask only when user has actually typed something, not just on focus
  // Check if displayValue has actual digits, not just mask placeholders
  const hasActualContent = displayValue && /\d/.test(displayValue);
  const showMask = hasStartedTyping || hasActualContent;

  return (
    <div>
      <Label htmlFor="date-of-birth" text={label} />

      <IMaskInput
        id="date-of-birth"
        className={`h-12 w-full py-2 px-3 border-0 dark:bg-uniform-gray-700 light:bg-uniform-gray-100 dark:text-uniform-gray-50 light:text-uniform-gray-900 ${focusClasses} text-base rounded-md dark:scheme-dark light:scheme-light`}
        mask="MM/DD/YYYY"
        blocks={{
          MM: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
            maxLength: 2
          },
          DD: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31,
            maxLength: 2
          },
          YYYY: {
            mask: IMask.MaskedRange,
            from: 1900,
            to: CURRENT_YEAR,
            maxLength: 4
          }
        }}
        lazy={!showMask}
        autofix={false}
        placeholder="MM/DD/YYYY"
        value={displayValue}
        unmask={false}
        onAccept={(value: string) => {
          // Check if the value contains any actual digits (not just mask placeholders)
          const hasDigits = /\d/.test(value);

          // Mark that user has started typing when they enter any digit
          if (hasDigits && !hasStartedTyping) {
            setHasStartedTyping(true);
          }

          // Reset hasStartedTyping if user cleared all digits
          if (!hasDigits && hasStartedTyping) {
            setHasStartedTyping(false);
          }

          // Update the display value immediately to preserve partial input
          setDisplayValue(value);

          // Convert MM/DD/YYYY to YYYY-MM-DD for the form value
          const isoDate = convertToISOFormat(value);

          // Mark that this change came from user input
          // This prevents the useEffect from clearing the display
          isUserInputRef.current = true;

          // Always update the form value:
          // - Complete date: set the ISO date string
          // - Incomplete date: set empty string to trigger validation
          const event = { target: { value: isoDate } } as Pick<
            ChangeEvent<HTMLInputElement>,
            'target'
          >;
          onChange(event as ChangeEvent<HTMLInputElement>);
        }}
        onBlur={handleBlur}
        onFocus={handleFocus}
        inputMode="numeric"
      />
    </div>
  );
};

const DateOfBirthInput = <T extends FieldValues>({
  control,
  fieldError,
  label,
  name,
  rules
}: Props<T>) => {
  const { t } = useTranslate(`${COMPONENTS_SHARED_PATH_TRANSLATION_KEY}.DateOfBirthInput`);

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <MaskedDateInput
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            label={label || t('dateOfBirthLabel')}
          />
        )}
        rules={rules}
      />

      <FieldError error={fieldError} />
    </>
  );
};

export default DateOfBirthInput;
